import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import {
  useAuth,
  useIsWorkspacePermissionsResolving,
  usePermissions,
  usePermissionsReconciling,
  type Permissions,
} from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { ROUTES } from '@/shared/config/routes';
import { Spinner } from '@monksflow/monks-ui';

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Returns true if the user has access, false otherwise.
   *
   * @example
   * <ProtectedRoute canAccess={(p) => p.isInstanceAdmin || p.canManageAudiences}>
   */
  canAccess: (permissions: Permissions) => boolean;
  /**
   * Hold the redirect until the active workspace's permissions have resolved. Set it when
   * `canAccess` reads a workspace-scoped permission, which is empty (denied) until then.
   */
  awaitWorkspacePermissions?: boolean;
}

/**
 * Route guard that redirects to home if user is unauthenticated or lacks permission.
 * Shows loading spinner while fetching auth state.
 */
export const ProtectedRoute = ({
  children,
  canAccess,
  awaitWorkspacePermissions = false,
}: ProtectedRouteProps) => {
  const { value: user, fetching } = useAuth((s) => s.signedInUser);
  const permissions = usePermissions();
  const reconciling = usePermissionsReconciling();
  const workspaceId = useCurrentWorkspaceId();
  const workspacePermissionsResolving = useIsWorkspacePermissionsResolving(workspaceId);

  if (fetching || reconciling || (awaitWorkspacePermissions && workspacePermissionsResolving)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  // Redirect to home if not authenticated or lacks permission
  if (!user || !canAccess(permissions)) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};
